import type { Country } from "react-phone-number-input";

import { CreditCardIcon } from "lucide-react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Controller } from "react-hook-form";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

import type {
  AttachCustomerPayload,
  SubscriptionFormHandle,
  SubscriptionFormProps,
} from "@/features/auth/subscription/components/SubscriptionType.ts";

import { FieldError, Input, Label, TextField } from "@/components/field";
import { env } from "@/config/env";
import { pushDataLayer } from "@/lib/dataLayer";

import type {
  CardChangeEventType,
  ElementsType,
  ElementType,
  StripeInterface,
  StripeType,
} from "./StripeInterface.ts";

import { EmailInput } from "../../onboarding/components/EmailInput";
import { sendPurchaseConfirmed } from "../api/purchaseConfirmed.api";
import {
  type SubscriptionFormData,
  useSubscriptionForm,
} from "../hooks/useSubscriptionForm";

const PAYMENT_METHODS = [
  { icon: CreditCardIcon, id: "card", label: "Card Information" },
] as const;

function getBrowserCountry(): Country | undefined {
  const lang = navigator.language || "en-US";
  const parts = lang.split("-");

  return parts[1]?.toUpperCase() as Country;
}

function extractStripeIds(customerData: unknown): {
  invoiceId: string | undefined;
  subscriptionId: string | undefined;
} {
  if (!customerData || typeof customerData !== "object") {
    return { invoiceId: undefined, subscriptionId: undefined };
  }

  const { invoice, invoice_id, subscription, subscription_id } =
    customerData as {
      invoice?: { id?: string };
      invoice_id?: string;
      subscription?: {
        id?: string;
        latest_invoice?: { id?: string } | string;
        latest_invoice_id?: string;
      };
      subscription_id?: string;
    };
  const latestInvoice =
    typeof subscription?.latest_invoice === "string"
      ? subscription.latest_invoice
      : subscription?.latest_invoice?.id;
  const invoiceId =
    subscription?.latest_invoice_id ??
    latestInvoice ??
    invoice?.id ??
    invoice_id;
  const subscriptionId = subscription?.id ?? subscription_id;

  if (typeof invoiceId !== "string" || typeof subscriptionId !== "string") {
    console.warn(
      "Could not find Stripe invoice/subscription ids in the attach-customer response. Top-level keys:",
      Object.keys(customerData as Record<string, unknown>),
    );
  }

  return {
    invoiceId: typeof invoiceId === "string" ? invoiceId : undefined,
    subscriptionId:
      typeof subscriptionId === "string" ? subscriptionId : undefined,
  };
}

const SubscriptionForm = forwardRef<
  SubscriptionFormHandle,
  SubscriptionFormProps
>(
  (
    {
      onSubmit,
      onSubmittingChange,
      promotionCode,
      promotionCodeId,
      promotionRef,
      subscriptionApiData,
    },
    ref,
  ) => {
    const { control, formState, handleSubmit, setValue, watch } =
      useSubscriptionForm();
    const { dirtyFields, errors } = formState;

    const paymentMethod = watch("paymentMethod");
    const fullName = watch("fullName");
    const companyName = watch("companyName");
    const email = watch("email");
    const phoneNumber = watch("phoneNumber");
    const contactSectionEventSentRef = useRef(false);
    const stripeRef = useRef<null | StripeType>(null);
    const elementsRef = useRef<ElementsType | null>(null);
    const cardRef = useRef<ElementType | null>(null);
    const cardChangeHandlerAttachedRef = useRef<boolean>(false);
    const [stripeError, setStripeError] = useState<null | string>(null);

    const publishableKeyProp =
      subscriptionApiData?.publishable_key || undefined;
    const publishableKey = (publishableKeyProp ||
      (import.meta as ImportMeta).env?.VITE_STRIPE_PUBLISHABLE_KEY) as
      | string
      | undefined;

    useEffect(() => {
      if (!publishableKey) {
        console.warn("Missing VITE_STRIPE_PUBLISHABLE_KEY env var");

        return;
      }

      // verify if stripe scripts were initialized
      const StripeCtor = (window as StripeInterface).Stripe;

      if (!StripeCtor) return;

      if (!stripeRef.current) {
        stripeRef.current = StripeCtor(publishableKey);
      }
      if (!elementsRef.current && stripeRef.current) {
        elementsRef.current = stripeRef.current.elements();
      }

      // Mount Card when selected
      if (paymentMethod === "card") {
        if (elementsRef.current) {
          // Reuse an existing element if Stripe already has one
          const existing =
            elementsRef.current.getElement &&
            elementsRef.current.getElement("card");

          if (existing && !cardRef.current) {
            cardRef.current = existing;
          }
        }
        if (!cardRef.current && elementsRef.current) {
          cardRef.current = elementsRef.current.create("card", {
            hidePostalCode: true,
          });
        }
        // Attach a change handler only once
        if (cardRef.current && !cardChangeHandlerAttachedRef.current) {
          try {
            cardRef.current?.on?.("change", (event: CardChangeEventType) => {
              setStripeError(event.error?.message ?? null);
            });
            cardChangeHandlerAttachedRef.current = true;
          } catch {}
        }
        // Mount only if the container is empty (avoid "Can only create one Element of type card")
        const container = document.querySelector("#card-element");
        const needsMount = container && container.childElementCount === 0;

        if (needsMount && cardRef.current) {
          cardRef.current.mount("#card-element");
        }
      }
    }, [
      paymentMethod,
      publishableKey,
      onSubmit,
      onSubmittingChange,
      subscriptionApiData,
      watch,
    ]);

    // Populate a form with API-derived data when available
    useEffect(() => {
      if (!subscriptionApiData) return;

      const { intent, prefill, price, product, publishable_key, quantity } =
        subscriptionApiData;

      if (intent) {
        setValue("paymentIntentId", intent.id);
        setValue("paymentIntentClientSecret", intent.client_secret);
        setValue("amount", intent.amount);
        setValue("currency", intent.currency);
      }

      if (product) {
        setValue("productId", product.id);
      }
      if (price) {
        setValue("priceId", price.id);
      }
      if (typeof quantity === "number") setValue("quantity", quantity);
      if (publishable_key) setValue("publishableKey", publishable_key);

      if (prefill) {
        if (prefill.name) setValue("fullName", prefill.name);
        if (prefill.company_name) setValue("companyName", prefill.company_name);
        if (prefill.email) setValue("email", prefill.email);
        if (prefill.phone) {
          const normalizedPhone = prefill.phone.startsWith("+")
            ? prefill.phone
            : `+${prefill.phone}`;

          setValue("phoneNumber", normalizedPhone);
        }
      }
    }, [subscriptionApiData, setValue]);

    useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      const receiptEmail = params.get("receipt_email");

      if (!receiptEmail) return;

      const backendEmail = subscriptionApiData?.prefill?.email ?? null;

      if (!backendEmail) {
        setValue("email", receiptEmail);
      }
    }, [setValue, subscriptionApiData]);

    useEffect(() => {
      if (contactSectionEventSentRef.current) return;

      const completed = [fullName, companyName, email, phoneNumber].every(
        (value) => !!value?.trim(),
      );
      const edited = !!(
        dirtyFields.fullName ||
        dirtyFields.companyName ||
        dirtyFields.email ||
        dirtyFields.phoneNumber
      );
      const valid = !(
        errors.fullName ||
        errors.companyName ||
        errors.email ||
        errors.phoneNumber
      );

      if (!completed || !edited || !valid) return;

      contactSectionEventSentRef.current = true;
      pushDataLayer({
        event: "checkout_form_interaction",
        page_url: "subscription",
        section: "checkout_form",
      });
    }, [fullName, companyName, email, phoneNumber, dirtyFields, errors]);

    const onSubmitInternal = async (data: SubscriptionFormData) => {
      onSubmittingChange?.(true);

      if (data.paymentMethod === "card") {
        let invoiceId: string | undefined;
        let subscriptionId: string | undefined;

        if (!publishableKey) {
          setStripeError(
            "Payment configuration error. Please try again later.",
          );
          onSubmittingChange?.(false);

          return;
        }
        if (!stripeRef.current || !cardRef.current) {
          setStripeError(
            "Payment form is not ready yet. Please wait a moment.",
          );
          onSubmittingChange?.(false);

          return;
        }

        const billingName = data.nameOnCard || data.fullName;
        const { error, paymentMethod: pm } =
          await stripeRef.current.createPaymentMethod({
            billing_details: {
              email: data.email,
              name: billingName,
            },
            card: cardRef.current,
            type: "card",
          });

        if (error || !pm) {
          setStripeError(error?.message || "Unable to create payment method");
          onSubmittingChange?.(false);

          return;
        }

        setValue("stripePaymentMethodId", pm.id, { shouldValidate: true });

        let clientSecret =
          subscriptionApiData?.intent?.client_secret ||
          data.paymentIntentClientSecret;

        if (!clientSecret) {
          setStripeError("Missing payment intent. Please refresh the page.");
          onSubmittingChange?.(false);

          return;
        }

        // create/attach customer to correct intent
        try {
          const base = env.VITE_PHP_URL.replace(/\/$/, "");

          // Resolve promo values at submitting time, preferring ref over props
          const resolvedPromotionCode =
            promotionRef?.current?.code || promotionCode || null;
          const resolvedPromotionCodeId =
            promotionRef?.current?.id || promotionCodeId || null;

          const payload: AttachCustomerPayload = {
            company_name: data.companyName,
            email: data.email,
            name: data.fullName,
            payment_method_id: pm.id,
            phone: data.phoneNumber,
            promotion_code: resolvedPromotionCode,
            promotion_code_id: resolvedPromotionCodeId,
            setup_intent_id: subscriptionApiData?.intent?.id,
          };

          //TODO transfer this service to Node
          const response = await fetch(`${base}/api/stripe/attach-customer`, {
            body: JSON.stringify(payload),
            headers: { "Content-Type": "application/json" },
            method: "POST",
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));

            throw new Error(
              errorData.message || "Failed to create/attach customer",
            );
          }

          const customerData = await response.json();

          ({ invoiceId, subscriptionId } = extractStripeIds(customerData));
          // Some backends return an updated client_secret after attaching the customer
          const updatedSecret =
            customerData?.intent?.client_secret || customerData?.client_secret;

          if (
            typeof updatedSecret === "string" &&
            updatedSecret.length > 0 &&
            updatedSecret !== clientSecret
          ) {
            console.log(
              "Using updated client_secret from backend after attach.",
            );
            clientSecret = updatedSecret;
          }
        } catch (error_: unknown) {
          console.error("Customer creation/attachment error:", error_);
          const message =
            typeof error_ === "object" && error_ && "message" in error_
              ? String((error_ as { message?: unknown }).message ?? "")
              : "";

          setStripeError(message || "Failed to process customer information");
          onSubmittingChange?.(false);

          return;
        }

        const result = await stripeRef.current.confirmCardPayment(
          clientSecret,
          {
            payment_method: pm.id,
          },
        );

        if (result.error) {
          setStripeError(result.error.message || null);
          onSubmittingChange?.(false);

          return;
        }

        //TODO remove consoles after testing.
        try {
          console.log("[MetaCAPI] amount sources", {
            "data.amount": data.amount,
            "promotionRef.current?.finalAmountCents":
              promotionRef?.current?.finalAmountCents,
            "subscriptionApiData?.intent": subscriptionApiData?.intent,
            "subscriptionApiData?.price": subscriptionApiData?.price,
          });

          const amountCents =
            promotionRef?.current?.finalAmountCents ??
            subscriptionApiData?.price?.unit_amount ??
            subscriptionApiData?.intent?.amount ??
            data.amount ??
            0;

          const purchaseConfirmedPayload = {
            amount: amountCents / 100,
            currency:
              subscriptionApiData?.price?.currency ??
              subscriptionApiData?.intent?.currency ??
              (data.currency || undefined),
            email: data.email,
            invoiceId: invoiceId || subscriptionId || "pending",
            productId: data.productId || "",
          };

          console.log(
            "[MetaCAPI] sending purchase-confirmed",
            purchaseConfirmedPayload,
          );

          const response = await sendPurchaseConfirmed(
            purchaseConfirmedPayload,
          );

          console.log(
            "[MetaCAPI] purchase-confirmed response",
            response.status,
          );
        } catch (error_) {
          console.error(
            "[MetaCAPI] Failed to send purchase confirmation:",
            error_,
          );
        }

        await onSubmit({
          ...data,
          invoiceId,
          stripePaymentMethodId: pm.id,
          subscriptionId,
        });

        return;
      }
      await onSubmit(data);
    };

    useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          console.log("Form values:", watch());
          await handleSubmit(onSubmitInternal, (errors) => {
            console.log("Validation errors:", errors);
          })();
        },
      }),
      [handleSubmit, onSubmitInternal],
    );

    const browserCountry = getBrowserCountry();

    return (
      <div className="w-full">
        <form className="space-y-8" onSubmit={handleSubmit(onSubmitInternal)}>
          {/* Contact Information */}
          <h3 className="text-md font-medium text-text-tertiary mb-1">
            Contact Information
          </h3>
          <div className="space-y-6 border-[1px] border-[#D0D5DD] rounded-2xl p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Controller
                control={control}
                name="fullName"
                render={({
                  field: { name, onBlur, onChange, ref, value },
                  fieldState: { error, invalid },
                }) => (
                  <TextField
                    isInvalid={invalid}
                    isRequired
                    name={name}
                    onBlur={onBlur}
                    onChange={onChange}
                    validationBehavior="aria"
                    value={value}
                  >
                    <Label>Full name</Label>
                    <Input placeholder="Enter your full name" ref={ref} />
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />

              <Controller
                control={control}
                name="companyName"
                render={({
                  field: { name, onBlur, onChange, ref, value },
                  fieldState: { error, invalid },
                }) => (
                  <TextField
                    isInvalid={invalid}
                    isRequired
                    name={name}
                    onBlur={onBlur}
                    onChange={onChange}
                    validationBehavior="aria"
                    value={value}
                  >
                    <Label>Company name</Label>
                    <Input placeholder="Enter the company name" ref={ref} />
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Controller
                control={control}
                name="email"
                render={({
                  field: { name, onBlur, onChange, ref, value },
                  fieldState: { error, invalid },
                }) => (
                  <TextField
                    isInvalid={invalid}
                    isRequired
                    name={name}
                    onBlur={onBlur}
                    onChange={onChange}
                    validationBehavior="aria"
                    value={value}
                  >
                    <Label>Email</Label>
                    <EmailInput
                      name={name}
                      onBlur={onBlur}
                      onChange={onChange}
                      onClear={() => onChange("")}
                      placeholder="Enter your email"
                      ref={ref}
                      value={value}
                    />
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />

              <Controller
                control={control}
                name="phoneNumber"
                render={({
                  field: { name, onChange, value },
                  fieldState: { error, invalid },
                }) => (
                  <TextField
                    isInvalid={invalid}
                    isRequired
                    name={name}
                    validationBehavior="aria"
                    value={value}
                  >
                    <Label>Telephone number</Label>
                    <PhoneInput
                      aria-invalid={invalid || undefined}
                      className={[
                        // Match Input styles on the container
                        "w-full text-text-primary rounded-md border outline-hidden border-border-primary bg-bg-primary",
                        "px-[14px] py-[10px]",
                        "text-md",
                        // Focus ring on container when inner input is focused
                        "focus-within:ring-4 focus-within:ring-border-brand/25 focus-within:border-border-brand",
                        // Error state
                        invalid
                          ? "border-border-error focus-within:ring-border-error/25 focus-within:border-border-error"
                          : "",
                        // Remove default lib spacing and align children
                        "flex items-center gap-2",
                        "[&_.PhoneInputInput]:w-full [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:outline-none",
                        "[&_.PhoneInputInput]:border-0 [&_.PhoneInputInput]:p-0 [&_.PhoneInputInput]:m-0",
                        "[&_.PhoneInputInput]:placeholder:text-text-placeholder",
                        // Country select/button styling to blend in
                        "[&_.PhoneInputCountry]:m-0 [&_.PhoneInputCountry]:me-2 [&_.PhoneInputCountry]:relative",
                        // Make native select cover the flag button but invisible so it's clickable
                        "[&_.PhoneInputCountrySelect]:absolute [&_.PhoneInputCountrySelect]:inset-0 [&_.PhoneInputCountrySelect]:opacity-0 [&_.PhoneInputCountrySelect]:cursor-pointer [&_.PhoneInputCountrySelect]:block",
                      ].join(" ")}
                      countryCallingCodeEditable={false}
                      data-ui="control"
                      defaultCountry={browserCountry}
                      international
                      onChange={(val) => onChange(val || "")}
                      placeholder="Enter your phone number"
                      value={value || ""}
                    />
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />
            </div>
          </div>

          {/* Payment Method */}
          <h3 className="text-md font-medium text-text-tertiary mb-1">
            Payment method
          </h3>
          <div className="space-y-6 border-[1px] border-[#D0D5DD] rounded-2xl">
            <div className="space-y-4">
              {/* Payment Method Selection */}
              <Controller
                control={control}
                name="paymentMethod"
                render={({
                  field: { name, onChange, value },
                  fieldState: { error },
                }) => (
                  <div className="space-y-3">
                    {PAYMENT_METHODS.map((method) => (
                      <div className="space-y-3" key={method.id}>
                        <label className="flex items-center gap-3 p-4 rounded-lg cursor-pointer transition-colors mb-0">
                          <input
                            checked={value === method.id}
                            className="sr-only"
                            name={name}
                            onChange={() => onChange(method.id)}
                            type="radio"
                            value={method.id}
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              value === method.id
                                ? "border-purple-600"
                                : "border-black"
                            }`}
                          >
                            {value === method.id && (
                              <div className="w-2.5 h-2.5 rounded-full bg-purple-600" />
                            )}
                          </div>
                          {method.icon && (
                            <div
                              className="w-12 h-12 rounded-lg border flex items-center justify-center"
                              style={{ borderColor: "#EAECF0" }}
                            >
                              <method.icon className="w-6 h-6 text-text-tertiary" />
                            </div>
                          )}
                          <span className="text-[18px] font-medium text-black">
                            {method.label}
                          </span>
                        </label>

                        {/* Payment method specific form */}
                        {value === method.id && method.id === "card" && (
                          <div className="space-y-4 p-4">
                            <div className="space-y-4">
                              <div>
                                <Label>Card details</Label>
                                <div
                                  className="mt-2 p-3 border rounded-md"
                                  id="card-element"
                                  style={{ borderColor: "#D0D5DD" }}
                                />
                                {stripeError && (
                                  <div className="text-danger text-sm mt-2">
                                    {stripeError}
                                  </div>
                                )}
                              </div>

                              <Controller
                                control={control}
                                name="nameOnCard"
                                render={({
                                  field: { name, onBlur, onChange, ref, value },
                                  fieldState: { error, invalid },
                                }) => (
                                  <TextField
                                    isInvalid={invalid}
                                    isRequired
                                    name={name}
                                    onBlur={onBlur}
                                    onChange={onChange}
                                    validationBehavior="aria"
                                    value={value}
                                  >
                                    <Label>Name on card</Label>
                                    <Input placeholder="John Doe" ref={ref} />
                                    <FieldError>{error?.message}</FieldError>
                                  </TextField>
                                )}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {error && <FieldError>{error.message}</FieldError>}
                  </div>
                )}
              />
            </div>
          </div>
        </form>
      </div>
    );
  },
);

export default SubscriptionForm;

SubscriptionForm.displayName = "SubscriptionForm";
